import { fileService } from "@/services/ServiceRegistry";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { useCreateHotelService } from "@/hooks/useHotelServices";
import {
  checkInOutAvailabilityFromDisplayRow,
  CreateHotelServiceFormData,
  mapServiceDisplaySettingsToRequest,
} from "../utils/serviceSchema";
import { CreateHotelServiceRequest } from "@/types/hotelService";

export const useServiceCreate = () => {
  const navigate = useNavigate();
  const createHotelServiceMutation = useCreateHotelService();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleHotelServiceCreate = async (
    data: CreateHotelServiceFormData,
    hotelId: string,
    selectedPhotoFile: File | null,
  ) => {
    try {
      setIsLoading(true);
      const { price, discount, contractor, serviceDisplaySettings, ...rest } =
        data;

      const serviceData = Object.fromEntries(
        Object.entries(rest).filter(([key]) => key !== "photoId"),
      ) as Omit<
        CreateHotelServiceFormData,
        | "price"
        | "discount"
        | "contractor"
        | "serviceDisplaySettings"
        | "photoId"
      >;

      const { checkinAvailable, checkoutAvailable } =
        checkInOutAvailabilityFromDisplayRow(
          serviceDisplaySettings.checkInCheckOut,
        );

      let photoFileId = null;

      if (selectedPhotoFile) {
        try {
          photoFileId =
            await fileService.uploadFileWithPresignedUrl(selectedPhotoFile);
        } catch (photoError) {
          console.error("Failed to upload photo:", photoError);
        }
      }

      const serviceRequest: CreateHotelServiceRequest = {
        ...serviceData,
        checkinAvailable,
        checkoutAvailable,
        displaySettings: mapServiceDisplaySettingsToRequest(
          serviceDisplaySettings,
        ),
        ...(photoFileId && { photoId: photoFileId }),
        ...(price && { price: parseFloat(price) }),
        ...(discount && { discount: parseFloat(discount) }),
        ...(contractor && {
          contractor: {
            ...contractor,
            fee: parseFloat(contractor.fee),
          },
        }),
        hotelId: hotelId!,
      };

      // Create the room using the service
      await createHotelServiceMutation.mutateAsync(serviceRequest);

      // Navigate back to service list
      navigate("/services");
    } catch (error) {
      toast.error((error as Error).message);
      setError((error as Error).message);
      console.error("Error creating service:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    handleHotelServiceCreateSubmit: handleHotelServiceCreate,
    isLoading: isLoading,
    error: error,
  };
};
